import { z } from "zod";
import { AccountId, ApiKeyId } from "./ids.ts";

/**
 * Closed set of capabilities a programmatic API key may carry (#32). A key is
 * issued with a non-empty subset; the edge checks the relevant scope before
 * serving a request. Kept deliberately coarse — one scope per resource family,
 * not per route — so the consent screen stays legible. Extend by adding a
 * member here (and a route guard), never by overloading an existing one.
 */
export const ApiKeyScope = z.enum([
  // Read wallets, balances, and transaction history.
  "wallet:read",
  // Create/configure wallets + sub-wallets, set wallet status.
  "wallet:write",
  // Authorize / post / reverse transactions (spend).
  "transaction:write",
  // Mint sub-wallet-scoped MCP agent tokens.
  "agent:provision",
]);
export type ApiKeyScope = z.output<typeof ApiKeyScope>;

/**
 * Lifecycle of an API key. `active` keys authenticate; `revoked` is terminal
 * (an explicit rotation/kill by the owner). Expiry is NOT a status — it is
 * derived from `expiresAt` at verify time, so a lapsed key needs no sweep to
 * stop working.
 */
export const ApiKeyStatus = z.enum(["active", "revoked"]);
export type ApiKeyStatus = z.output<typeof ApiKeyStatus>;

/**
 * A programmatic API key record (#32) — the management view, never the secret.
 * The raw key is shown exactly once at issuance; at rest we keep only its
 * SHA-256 hash (see `@ggcoin/auth` `api-key-token`). `prefix` is the
 * non-secret leading slice (e.g. `gg_a1b2c3`) shown in dashboards so an owner
 * can tell keys apart without ever seeing the secret again.
 */
export const ApiKey = z.object({
  id: ApiKeyId,
  accountId: AccountId,
  /** Owner-facing label, e.g. "ci-deploy" or "production server". */
  label: z.string().min(1).max(80),
  /** Non-secret leading slice for display/disambiguation. */
  prefix: z.string().min(1).max(32),
  /** Non-empty set of granted capabilities — no duplicates. */
  scopes: z
    .array(ApiKeyScope)
    .min(1)
    .refine((s) => new Set(s).size === s.length, "scopes must be unique"),
  status: ApiKeyStatus,
  // Strict `z.date()` (not `z.coerce.date()`): the domain contract is Date
  // objects; string/number coercion belongs at the api / persistence boundary.
  createdAt: z.date(),
  /** Null means the key never expires. */
  expiresAt: z.date().nullable(),
  /**
   * Per-UTC-day request quota (#32). Null = unlimited. Enforced on the verify
   * hot path against a rolling daily counter; the (quota+1)-th call in a day is
   * rejected (429). Per-key so a noisy integration can be capped in isolation.
   */
  quotaPerDay: z.number().int().positive().nullable(),
});
export type ApiKey = z.output<typeof ApiKey>;
